import React, { useRef } from "react";
import { Instagram } from "lucide-react";
import FormLabel from "../FormLabel";
import FormFieldError, { fieldClass } from "../FormFieldError";
import CityAutocomplete from "../CityAutocomplete";
import InstagramInput from "../InstagramInput";
import { PROFILE_HOST_FIELD_IDS } from "../../lib/profileForm";

/**
 * Shared school profile fields (name, city, bio, Instagram, WhatsApp).
 * Controlled — parent owns values and save.
 */
export default function SchoolProfileForm({
  values,
  onChange,
  errors = {},
  showWhatsApp = true,
  cityPlaceholder = "Berlin",
  nameTestId = "school-name-input",
  cityTestId = "school-city-input",
  bioTestId = "school-bio-input",
  instagramTestId = "school-instagram-input",
  whatsappTestId = "school-whatsapp-input",
  children,
}) {
  // Keep latest values for sequential city onChange + onResolvedChange in one keystroke/pick.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  function commit(patch) {
    const next = { ...valuesRef.current, ...patch };
    valuesRef.current = next;
    onChange?.(next);
  }

  function update(key, value) {
    commit({ [key]: value });
  }

  return (
    <div className="space-y-4">
      <label className="block">
        <FormLabel className="block mb-2">School name *</FormLabel>
        <input
          id={PROFILE_HOST_FIELD_IDS.school_name}
          className={fieldClass(errors.name)}
          value={values.name || ""}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Sala Caracas Dance Academy"
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? `${PROFILE_HOST_FIELD_IDS.school_name}-error` : undefined}
          data-testid={nameTestId}
        />
        {errors.name ? (
          <FormFieldError id={`${PROFILE_HOST_FIELD_IDS.school_name}-error`}>
            {errors.name}
          </FormFieldError>
        ) : null}
      </label>

      <div className="block">
        <FormLabel as="label" htmlFor={PROFILE_HOST_FIELD_IDS.school_city} className="block mb-2">
          School city *
        </FormLabel>
        <CityAutocomplete
          value={values.city || ""}
          onChange={(v) => {
            const prev = valuesRef.current;
            commit({
              city: v,
              city_location: v !== prev.city ? null : prev.city_location,
            });
          }}
          onResolvedChange={(loc) => {
            if (loc) commit({ city: loc.name, city_location: loc });
            else commit({ city_location: null });
          }}
          placeholder={cityPlaceholder}
          testId={cityTestId}
          inputId={PROFILE_HOST_FIELD_IDS.school_city}
          error={errors.city}
          required
        />
      </div>

      <label className="block">
        <FormLabel className="block mb-2">Short bio</FormLabel>
        <textarea
          className="field min-h-[80px]"
          value={values.bio || ""}
          onChange={(e) => update("bio", e.target.value)}
          placeholder="Cuban + LA salsa school in Prenzlauer Berg."
          data-testid={bioTestId}
        />
      </label>

      <label className="block">
        <FormLabel className="block mb-2 flex items-center gap-1">
          <Instagram className="w-3.5 h-3.5" strokeWidth={2} aria-hidden /> School Instagram
        </FormLabel>
        <InstagramInput
          id="school-instagram-input"
          value={values.instagram || ""}
          onChange={(v) => update("instagram", v)}
          placeholder="salacaracas"
          data-testid={instagramTestId}
        />
      </label>

      {showWhatsApp ? (
        <label className="block">
          <FormLabel className="block mb-2">WhatsApp community link</FormLabel>
          <input
            className="field"
            value={values.whatsapp_url || ""}
            onChange={(e) => update("whatsapp_url", e.target.value)}
            placeholder="https://chat.whatsapp.com/…"
            data-testid={whatsappTestId}
          />
        </label>
      ) : null}

      {children}
    </div>
  );
}
